import { memo, useContext, useEffect } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Button } from '@pulse-web-ui/button';
import { RGSNoWIFI } from '@pulse-web-ui/icons';

import { HeaderAdaptive1 } from '@src/components';
import { sendAnalyticEvent } from '@src/components/web-analytic';
import { PAYMENT_ROUTE, analyticEvents } from '@src/constants';
import { Store } from '@src/store';
import { WizardActionTypes } from '@src/store/wizard';
import { getProductForAnalytic, useScrollToIframeOnMount } from '@src/utils';

import {
  GlobalErrorContent,
  GlobalErrorFormSub,
  GlobalErrorWrapper,
} from './global-error-info.styles';

const getCurrentStep = (pathname: string, wizardStep: number) => {
  if (pathname === '/order-detail') return wizardStep + 1;
  else if (pathname === '/order-checkout') return wizardStep + 2;
  else if (pathname === PAYMENT_ROUTE) return 'pay-error';
  else if (pathname === '/personal-info') return 'personal-info-error';
  else return wizardStep;
};

type Props = {
  retryHandler?: () => void;
};

export const GlobalErrorInfo: FC<Props> = memo(({ retryHandler }) => {
  const { t } = useTranslation();
  const {
    state: {
      stateUser: { selectedProduct },
      stateWizard: { currentStep },
    },
    dispatch,
  } = useContext(Store);
  const { pathname } = useLocation();

  const handleRetry = () => {
    if (retryHandler) {
      retryHandler();
    }
  };

  useScrollToIframeOnMount();

  useEffect(() => {
    dispatch({
      type: WizardActionTypes.SetFwNavDisabled,
      payload: true,
    });

    dispatch({
      type: WizardActionTypes.SetGlobalError,
      payload: true,
    });

    const timeout = setTimeout(() => {
      sendAnalyticEvent(analyticEvents.somethingWentWrong, {
        product: getProductForAnalytic(selectedProduct),
        screen: getCurrentStep(pathname, currentStep),
      });
    }, 1000);

    return () => {
      clearTimeout(timeout);
      dispatch({
        type: WizardActionTypes.SetFwNavDisabled,
        payload: false,
      });

      dispatch({
        type: WizardActionTypes.SetGlobalError,
        payload: false,
      });
    };
  }, []);

  return (
    <GlobalErrorWrapper>
      <RGSNoWIFI width={64} />
      <GlobalErrorContent>
        <HeaderAdaptive1 align="center">
          {t('AUTH:headers.whenLoadingData')}
          <br />
          {t('AUTH:headers.somethingWentWrong')}
        </HeaderAdaptive1>
        <GlobalErrorFormSub>
          {t('AUTH:hints.checkConnectionQuality')}
        </GlobalErrorFormSub>
      </GlobalErrorContent>
      {retryHandler ? (
        <Button
          variant="primary"
          label={t('COMMON:buttons.tryAgain') || ''}
          onClick={handleRetry}
        />
      ) : (
        <></>
      )}
    </GlobalErrorWrapper>
  );
});
